import type { ReactNode } from "react";
import AcceleratorDoc from "../../models/AcceleratorDoc";
import { DocFieldConfig } from "../../models/config/DocFieldConfig";
import { ExtractedFieldData } from "../../models/extraction/ExtractedFieldData";
import { HighlightColours } from "../../models/HighlightColors";
import { DlfMatcher } from "../../models/matchers/DlfMatcher";
import { Bubble } from "../bubbles/Bubble";
import { FieldStatusBubble } from "../bubbles/FieldStatusBubble";
import { TransformedStatusBubble } from "../bubbles/TransformedStatusBubble";
import { Button } from "../buttons/Button";
import { useAsync } from "../hooks/useAsync";
import { ReuseControl } from "../reuse/ReuseControl";
import { AdvancedTooltip } from "../tooltips/AdvancedTooltip";
import { DlfMatcherToggles } from "./DlfMatcherToggles";
import { KvExtendedDlfTypeChanger } from "./KvExtendedDlfTypeChanger";
import {
  TTClearButton,
  TTDlfMatcherTypeDropdown,
  TTFieldStatusLabel,
  TTEditButton,
  TTDlfGenerateReuse,
} from "../tooltips/ConfigurationTooltips";
import "../../styles/dlfEditing/dlfEditorHeader.css";
import { OcrWarningBubble } from "../bubbles/OcrWarningBubble";
import { InvalidBubble } from "../bubbles/InvalidBubble";

type Props = {
  /**
   * Controls colour of the editor Label
   */
  index: number;
  open: boolean;
  dlfMatcher: DlfMatcher;
  docFieldConfig: DocFieldConfig;
  extractedData: ExtractedFieldData;
  children: ReactNode;
  acceleratorDoc: AcceleratorDoc;
  changeSelectedReuse: (reuseIndex: number) => void;
  /**
   * Let the parent components know the Matcher has been updated and is ready to be persisted on the model
   */
  updateMatcher: (matcher: DlfMatcher) => void;
  changeOpen: (open: boolean) => void;
};

const DlfEditorHeader = (props: Props) => {
  const generateReuse = async () => {
    const data = await props.acceleratorDoc.generateDlfReuse(
      props.dlfMatcher.fieldName,
    );

    console.log(
      "data",
      data,
      props.dlfMatcher.getMatcherId(),
      props.dlfMatcher.getMatcherId() === data[0]?._matcherId,
      data[0]?._matcherId,
    );

    if (data.length > 0) {
      // 1 is first reuse option
      props.changeSelectedReuse(1);
    }
    return data;
  };

  const clear = () => {
    props.acceleratorDoc.clearDlf(props.dlfMatcher.fieldName);
    props.dlfMatcher.clear();
    props.updateMatcher(props.dlfMatcher);
  };

  const [generatedReuse, generateReuseTrigger, generatingReuse] = useAsync(
    generateReuse,
    null,
  );

  const isValid = props.extractedData.getAtIndex()?.valid;

  console.log(
    "props.extractedData",
    props.extractedData,
    props.extractedData.getAtIndex()?.fieldName,
    props.extractedData.getAtIndex(),
  );

  return (
    <>
      <tr className={"dlfEditorHeader"}>
        <td>
          <AdvancedTooltip
            short
            tooltipText={() => props.docFieldConfig.description}
          >
            {(tProps) => (
              <span data-field-name {...tProps}>
                {props.docFieldConfig.description}
              </span>
            )}
          </AdvancedTooltip>

          <AdvancedTooltip tooltipText={TTFieldStatusLabel}>
            {(tProps) => (
              <FieldStatusBubble
                tProps={tProps}
                status={props.extractedData?.getAtIndex()?.fieldStatus}
              />
            )}
          </AdvancedTooltip>
          {props.extractedData.getAtIndex() &&
            props.extractedData.getAtIndex()!.valid == false && (
              <InvalidBubble />
            )}
          {props.extractedData.getAtIndex() &&
            props.extractedData.getAtIndex()!.hasOcrWarning && (
              <OcrWarningBubble />
            )}
          {props.extractedData?.getAtIndex()?.transformed && (
            <TransformedStatusBubble />
          )}
          {/* dropdown to change Matcher type */}
          <AdvancedTooltip tooltipText={TTDlfMatcherTypeDropdown}>
            {(tProps) => (
              <KvExtendedDlfTypeChanger
                tProps={tProps}
                key={props.dlfMatcher.getMatcherId()}
                numberOfSuggestions={
                  props.docFieldConfig.validator.validationRules.length
                }
                defaultMatcherThreshold={props.docFieldConfig.threshold}
                matcher={props.dlfMatcher}
                changedType={(matcher) => {
                  props.changeOpen(true);
                  props.updateMatcher(matcher);
                }}
              />
            )}
          </AdvancedTooltip>
        </td>
        {/* Display field value */}
        <td
          onClick={() => props.changeOpen(!props.open)}
          style={{
            backgroundColor: HighlightColours.getHighlightColor(props.index),
          }}
        >
          <span>{props.extractedData.getAtIndex()?.fieldValue}</span>
        </td>
        {/* Control cluster (open/close, reuse, clear) */}
        <td>
          <AdvancedTooltip tooltipText={TTEditButton} short overflow>
            {(tProps) => (
              <Button {...tProps} onClick={() => props.changeOpen(!props.open)}>
                {props.open ? "Hide" : "Edit"}
              </Button>
            )}
          </AdvancedTooltip>
          {/* object to toggle reuse */}
          <AdvancedTooltip tooltipText={TTDlfGenerateReuse} overflow>
            {(tProps) => (
              <ReuseControl
                tProps={tProps}
                changeToReuse={(reuseIndex: number) =>
                  props.changeSelectedReuse(reuseIndex)
                }
                extractedData={props.extractedData}
                generateReuse={() => generateReuseTrigger()}
                generatingReuse={generatingReuse}
              />
            )}
          </AdvancedTooltip>
          <AdvancedTooltip tooltipText={TTClearButton} short overflow>
            {(tProps) => (
              <Button
                {...tProps}
                onClick={() => {
                  clear();
                }}
              >
                Clear
              </Button>
            )}
          </AdvancedTooltip>
        </td>
      </tr>
      {/* FieldMatcher toggles and FieldMatcher Editor */}
      <tr
        className={"dlfEditorBody"}
        style={{
          display: props.open ? undefined : "none",
        }}
      >
        <td colSpan={3}>
          <DlfMatcherToggles
            matcher={props.dlfMatcher}
            updateMatcher={props.updateMatcher}
            extractionConfidence={props.extractedData.getAtIndex()?.confidence}
            ocrConfidence={props.extractedData.getAtIndex()?.ocrConfidence}
            defaultOcrConfidence={props.docFieldConfig.ocrConfidenceThreshold}
          />

          {props.children}
        </td>
      </tr>
    </>
  );
};
export { DlfEditorHeader };
